import React, { useState, useEffect, useCallback } from 'react';
import { getAuthUrl, exchangeCodeForToken } from '../services/auth';
import { AuthUser, UserSession } from '../types';

interface AuthProps {
  onAuthSuccess: (authUser: AuthUser) => void;
  userSession: UserSession | null;
}

const Auth: React.FC<AuthProps> = ({ onAuthSuccess, userSession }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAuthCode = useCallback(async (code: string, state: string, referralCode?: string) => {
    setIsLoading(true);
    setError(null);
    console.log('Processing auth code with state validation...');
    if (referralCode) {
      console.log('Referral code detected:', referralCode);
    }
    
    try {
      const authUser = await exchangeCodeForToken(code, state, referralCode);
      console.log('Auth data received, calling onAuthSuccess');
      onAuthSuccess(authUser);
      
      // Clear URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      console.log('Authentication completed successfully');
    } catch (err) {
      console.error('Auth code processing failed:', err);
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  }, [onAuthSuccess]);

  useEffect(() => {
    // Check for OAuth callback code in URL (handles direct callback to main page)
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    const referralCode = urlParams.get('ref');

    if (error) {
      setError(`Authentication failed: ${error}`);
      return;
    }

    if (code && state && !userSession?.isAuthenticated) {
      console.log('Found auth code and state in URL:', code, state?.substring(0, 10) + '...');
      handleAuthCode(code, state, referralCode || undefined);
      // Clean URL after processing
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (code && !state) {
      setError('OAuth state parameter missing - possible security issue');
    }
  }, [userSession, handleAuthCode]);

  const handleLogin = async () => {
    try {
      const authUrl = await getAuthUrl();
      console.log('OAuth URL:', authUrl); // Debug log
      
      // Extract referral code from current URL if present
      const urlParams = new URLSearchParams(window.location.search);
      const referralCode = urlParams.get('ref');
      
      // Open OAuth in new window and keep reference
      const authWindow = window.open(authUrl, 'googleAuth', 'width=500,height=600,scrollbars=yes,resizable=yes');
      
      if (!authWindow) {
        setError('Failed to open authentication window. Please allow popups and try again.');
        return;
      }
      
      // Listen for postMessage from popup
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
          return; // Ignore messages from other origins
        }
        
        if (event.data.type === 'OAUTH_SUCCESS' && event.data.code && event.data.state) {
          console.log('Got auth code and state from popup:', event.data.code, event.data.state?.substring(0, 10) + '...');
          authWindow.close();
          handleAuthCode(event.data.code, event.data.state, referralCode || undefined);
        } else if (event.data.type === 'OAUTH_ERROR') {
          setError(`Authentication failed: ${event.data.error}`);
          authWindow.close();
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      // Also poll for manual closure or cross-origin detection
      const checkForCompletion = setInterval(() => {
        try {
          if (authWindow.closed) {
            clearInterval(checkForCompletion);
            window.removeEventListener('message', handleMessage);
            console.log('Auth window was closed manually');
            return;
          }
          
          // Try to detect if we're back on our domain with auth code
          try {
            const url = new URL(authWindow.location.href);
            if (url.origin === window.location.origin) {
              const code = url.searchParams.get('code');
              const error = url.searchParams.get('error');
              
              if (error) {
                setError(`Authentication failed: ${error}`);
                authWindow.close();
                clearInterval(checkForCompletion);
                window.removeEventListener('message', handleMessage);
                return;
              }
              
              if (code) {
                const state = url.searchParams.get('state');
                if (!state) {
                  setError('OAuth state parameter missing - possible security issue');
                  authWindow.close();
                  clearInterval(checkForCompletion);
                  window.removeEventListener('message', handleMessage);
                  return;
                }
                
                console.log('Got auth code and state from popup:', code, state.substring(0, 10) + '...');
                clearInterval(checkForCompletion);
                window.removeEventListener('message', handleMessage);
                
                // Process auth code with state and close window
                handleAuthCode(code, state, referralCode || undefined).finally(() => {
                  if (!authWindow.closed) {
                    authWindow.close();
                    console.log('Auth window closed after token exchange');
                  }
                });
                return;
              }
            }
          } catch (crossOriginError) {
            // Cross-origin access errors are expected until redirect happens
            // Just continue polling
          }
        } catch (err) {
          console.error('Error checking auth window:', err);
        }
      }, 500);
      
      // Cleanup after 5 minutes
      setTimeout(() => {
        clearInterval(checkForCompletion);
        window.removeEventListener('message', handleMessage);
        if (authWindow && !authWindow.closed) {
          authWindow.close();
          console.log('Auth window closed due to timeout');
        }
      }, 300000);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate auth URL');
      console.error('Auth error:', err);
    }
  };

  if (userSession?.isAuthenticated) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-green-800">
              ✅ Successfully connected to Google Search Console!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Connect Google Search Console</h2>
        <p className="text-gray-600">Authenticate to access your Search Console data</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Completing authentication...</p>
        </div>
      ) : (
        <div className="space-y-6">
          <button
            onClick={handleLogin}
            className="w-full bg-gradient-to-r from-orange-400 to-pink-600 hover:from-orange-500 hover:to-pink-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Connect Google Search Console
          </button>

          <div className="text-center text-sm text-gray-600 leading-relaxed">
            <p>To log into Serprocket just click the button above and you're all done. This connects your Serprocket account to your Google Search Console account in read-only mode to get all your data. There's no seperate login - you access your Serprocket account by connecting to Google Search Console.</p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900 leading-relaxed text-center">
              Serprocket connects to Google Search Console in read-only mode via secure OAuth. We fetch your performance data on demand to power the dashboard and insights. We don't keep bulk GSC exports, but we do store your account details, connected properties, tracked keywords, AI-generated briefs, and to-do items so you can pick up where you left off.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Auth;